import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { ActivationPanel } from "@/components/admin/wi/assess/ActivationPanel";
import { CalibrationCard } from "@/components/admin/wi/assess/CalibrationCard";
import { AssessRules } from "@/components/admin/wi/assess/AssessRules";
import { AssessmentTable } from "@/components/admin/wi/assess/AssessmentTable";
import { AssessmentDrawer } from "@/components/admin/wi/assess/AssessmentDrawer";
import { btnGhost } from "@/components/admin/wi/ui";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";

export default function WiAssess() {
  const [ov, setOv] = useState(null);
  const [mode, setMode] = useState("shadow");
  const [decision, setDecision] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const loadOv = useCallback(() => adminApi.get("/admin/wi/assess/overview").then(({ data }) => setOv(data)).catch(() => {}), []);
  const loadRows = useCallback(() => adminApi.get("/admin/wi/assessments", { params: { mode, decision, page } }).then(({ data }) => setData(data)).catch(() => {}), [mode, decision, page]);
  const refresh = useCallback(() => { loadOv(); loadRows(); }, [loadOv, loadRows]);
  useEffect(() => { loadOv(); }, [loadOv]);
  useEffect(() => { loadRows(); }, [loadRows]);
  const busy = ov?.calibration_job?.status === "running" || ov?.live_job?.status === "running";
  usePolling(refresh, busy ? 5000 : 30000);
  const changeMode = (m) => { setMode(m); setPage(0); setData(null); };
  const changeDecision = (d) => { setDecision(d); setPage(0); };
  const checkNew = async () => {
    try { await adminApi.post("/admin/wi/assess/check-new"); toast.success("Checking Monday.com for new claims…"); setTimeout(refresh, 1500); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="wi-assess-page">
      <AdminHeader title="AI Assessments" subtitle="Shadow mode — the AI scores claims and suggests an outcome. Nothing is approved, emailed or written to Monday.com."
        actions={<button className={btnGhost} onClick={checkNew} disabled={!ov?.ai_assessment_enabled || ov?.emergency_pause || ov?.live_job?.status === "running"} data-testid="wi-check-new-btn"><ArrowsClockwise size={15} /> Check for new claims</button>} />
      {!ov ? <div className="p-8"><div className="h-40 animate-pulse bg-white/[0.02] rounded-2xl" /></div> : (
        <div className="p-8 space-y-6">
          <ActivationPanel ov={ov} onChange={(d) => { setOv(d); setTimeout(loadRows, 1500); }} />
          <div className="grid xl:grid-cols-3 gap-6 items-start">
            <div className="xl:col-span-2">
              <AssessmentTable mode={mode} setMode={changeMode} decision={decision} setDecision={changeDecision} data={data} page={page} setPage={setPage} onOpen={setOpen} />
            </div>
            <div className="space-y-6">
              <CalibrationCard ov={ov} onStarted={() => { loadOv(); changeMode("calibration"); }} />
              <AssessRules settings={ov.settings} onSaved={setOv} />
            </div>
          </div>
        </div>
      )}
      {open && <AssessmentDrawer row={open} paused={!!ov?.emergency_pause} onClose={() => setOpen(null)} onChanged={refresh} />}
    </div>
  );
}
